import type { Kernel, MathShim, Params, RNG } from "../sdk/kernel";

interface SirPartial {
  /** Sum of final attack-rate fractions across the trials in this chunk. */
  attackSum: number;
  /** Sum of peak-infected fractions across the trials in this chunk. */
  peakSum: number;
  /** Per-chunk attack-rate mean (one histogram sample per chunk). */
  trials: number;
}

// Population per stochastic trial. Each Monte Carlo iteration runs one full epidemic
// on a fresh population; the result averages the final attack rate across trials.
// POP × STEPS bounds the per-trial inner work — kept small (a few × 10⁴ float draws
// per trial) so default-N runs stay responsive; the gallery default N for SIR is set
// well below π's in the registry to keep a run sub-second on commodity hardware.
const STEPS = 120; // discrete time steps per trial (fixed — bounds per-trial work)

// SIR epidemic — stochastic compartment model. Each iteration simulates one outbreak in a
// population of POP, stepping S→I→R with per-individual Bernoulli draws, and reports the
// final attack rate (fraction ever infected). Uses only +, *, comparison and the rng:
// integer/arithmetic-only, so it is verifiable: true without the math shim. (The chunk-loop
// does its own binomial-by-counting rather than calling exp/log, keeping it bitwise-portable
// for M1; an exp-based fast path would move it onto the M3 fdlibm path.)
export const sir: Kernel<SirPartial> = {
  id: "sir",
  label: "SIR epidemic (stochastic)",
  verifiable: true,
  blurb: "Stochastic SIR outbreaks in a small population; reports the mean attack rate (fraction ever infected). Each iteration is a full epidemic, so it defaults to far fewer iterations than π.",
  resultUnit: "mean attack rate",
  defaultN: 2_000,
  chunkSize: 40, // heavy per-trial work → small chunks; ~50 chunks at default N

  params: [
    { key: "pop", label: "Population", default: 200, min: 20, max: 2000, step: 10, hint: "Individuals per stochastic outbreak. Larger = heavier per-trial work." },
    { key: "beta", label: "Transmission (β)", default: 0.35, min: 0.01, max: 2, step: 0.01, hint: "Transmission rate per infectious contact. β/γ is the basic reproduction number R₀." },
    { key: "gamma", label: "Recovery (γ)", default: 0.1, min: 0.01, max: 1, step: 0.01, hint: "Recovery rate per step (1 / infectious period)." },
  ],

  chunk(count: number, rng: RNG, _math: MathShim, params: Params): SirPartial {
    const POP = params.pop!;
    const BETA = params.beta!;
    const GAMMA = params.gamma!;
    let attackSum = 0;
    let peakSum = 0;
    for (let t = 0; t < count; t++) {
      let s = POP - 1;
      let i = 1;
      let r = 0;
      let peak = i;
      for (let step = 0; step < STEPS && i > 0; step++) {
        // Probability a given susceptible gets infected this step ≈ beta * i / POP.
        // Draw each new infection / recovery by counting Bernoulli successes — pure
        // comparison against rng.float(), no transcendental calls.
        const pInf = (BETA * i) / POP;
        let newInf = 0;
        for (let k = 0; k < s; k++) if (rng.float() < pInf) newInf++;
        let newRec = 0;
        for (let k = 0; k < i; k++) if (rng.float() < GAMMA) newRec++;
        s -= newInf;
        i += newInf - newRec;
        r += newRec;
        if (i > peak) peak = i;
      }
      attackSum += (POP - s) / POP;
      peakSum += peak / POP;
    }
    return { attackSum, peakSum, trials: count };
  },

  reduce(partials: SirPartial[]) {
    let attackSum = 0;
    let peakSum = 0;
    let trials = 0;
    const samples: number[] = [];
    for (const p of partials) {
      attackSum += p.attackSum;
      peakSum += p.peakSum;
      trials += p.trials;
      samples.push(p.trials > 0 ? p.attackSum / p.trials : 0); // per-chunk mean attack rate
    }
    // Final result: mean attack rate (fraction of the population ever infected).
    return { result: trials > 0 ? attackSum / trials : 0, samples };
  },
};
